// F6 workout logging. The backend has been complete for months — this is the
// UI it was waiting for. One exercise at a time: name it, then punch in sets.
// "Last time: 3×8 @ 60 kg" comes straight from the existing last_sessions map,
// which is the whole point of logging in the first place.
import { useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";
import { useToast } from "../lib/toast";
import { hapticTap, hapticSuccess } from "../lib/haptics";
import { SheetShell } from "./SheetShell";
import { Button } from "./ui/Button";
import type { TodayResponse } from "@shared/types";

interface DraftSet {
  reps: number;
  weight_kg: number;
  rpe: number | null;
}

interface Props {
  date: string;
  lastSessions: TodayResponse["last_sessions"];
  /** Exercises already logged today, for one-tap re-selection. */
  knownExercises: string[];
  onSaved: () => Promise<void> | void;
  onClose: () => void;
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
/** Exercise names are stored lowercase (functions/lib/workouts.ts); show them nicely. */
export const titleCase = (s: string) => s.replace(/\b[a-zäöüß]/g, (c) => c.toUpperCase());

export function WorkoutSheet({ date, lastSessions, knownExercises, onSaved, onClose }: Props) {
  const t = useT();
  const toast = useToast();
  const [exercise, setExercise] = useState("");
  const [sets, setSets] = useState<DraftSet[]>([{ reps: 8, weight_kg: 20, rpe: null }]);
  const [busy, setBusy] = useState(false);

  // Suggestions: what they did today, then everything they've ever logged.
  const suggestions = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const name of [...knownExercises, ...Object.keys(lastSessions)]) {
      const key = name.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(name);
    }
    return out.slice(0, 8);
  }, [knownExercises, lastSessions]);

  const last = lastSessions[exercise.trim().toLowerCase()];

  function pickExercise(name: string) {
    hapticTap();
    setExercise(name);
    const prev = lastSessions[name];
    // Seed the first set from last time — the fastest possible repeat.
    if (prev) setSets([{ reps: prev.reps, weight_kg: prev.weight_kg, rpe: null }]);
  }

  function patchSet(i: number, patch: Partial<DraftSet>) {
    setSets((cur) => cur.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  }

  function addSet() {
    hapticTap();
    setSets((cur) => [...cur, { ...cur[cur.length - 1], rpe: null }]);
  }

  async function save() {
    const name = exercise.trim();
    if (!name || busy) return;
    setBusy(true);
    try {
      await api.addWorkouts({
        performed_on: date,
        sets: sets.map((s) => ({
          exercise: name, reps: s.reps, weight_kg: s.weight_kg, rpe: s.rpe,
        })),
      });
      hapticSuccess();
      toast.success(t("toast.logged"));
      await onSaved();
      onClose();
    } catch {
      toast.error(t("toast.failed"));
      setBusy(false);
    }
  }

  const volume = sets.reduce((v, s) => v + s.reps * s.weight_kg, 0);

  return (
    <SheetShell onClose={onClose}>
      <h3 className="text-lg font-semibold mt-4 mb-4">{t("workout.log_title")}</h3>

      <label className="block mb-3">
        <span className="text-sm text-fg-muted uppercase tracking-wider">{t("workout.exercise")}</span>
        <input
          className="input mt-1"
          value={exercise}
          onChange={(e) => setExercise(e.target.value)}
          placeholder={t("workout.exercise_placeholder")}
          autoFocus
        />
      </label>

      {suggestions.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {suggestions.map((name) => (
            <button
              key={name}
              type="button"
              onClick={() => pickExercise(name)}
              className={`px-3 min-h-[44px] rounded-xl text-sm press ${
                exercise === name ? "bg-brand text-on-brand" : "bg-surface text-fg-secondary"
              }`}
            >
              {titleCase(name)}
            </button>
          ))}
        </div>
      )}

      {last && (
        <p className="text-sm text-fg-muted mb-3">
          {t("workout.last_time", {
            reps: String(last.reps),
            weight: String(last.weight_kg),
            date: new Date(last.date + "T12:00:00Z").toLocaleDateString(),
          })}
        </p>
      )}

      <div className="space-y-2 mb-3">
        {sets.map((s, i) => (
          <div key={i} className="flex items-center gap-2 bg-surface rounded-xl px-3 py-2">
            <span className="w-6 text-sm text-fg-muted tabular-nums">{i + 1}</span>
            <NumField
              label={t("workout.reps")}
              value={s.reps}
              step={1}
              onChange={(v) => patchSet(i, { reps: clamp(v, 1, 100) })}
            />
            <span className="text-fg-faint">×</span>
            <NumField
              label={t("workout.weight")}
              value={s.weight_kg}
              step={2.5}
              onChange={(v) => patchSet(i, { weight_kg: clamp(v, 0, 500) })}
            />
            {sets.length > 1 && (
              <button
                type="button"
                onClick={() => setSets((cur) => cur.filter((_, j) => j !== i))}
                aria-label={t("common.delete")}
                className="text-fg-faint px-2 py-2 -my-1 press"
              >✕</button>
            )}
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={addSet}
        className="w-full min-h-[44px] rounded-xl bg-surface text-sm font-medium press active:bg-surface-strong mb-3 flex items-center justify-center gap-1.5"
      >
        <Plus size={16} aria-hidden="true" />
        {t("workout.add_set")}
      </button>

      <p className="text-sm text-fg-muted mb-3 tabular-nums">
        {t("workout.volume", { kg: String(Math.round(volume)) })}
      </p>

      <Button fullWidth onClick={save} disabled={!exercise.trim() || busy}>
        {busy ? t("common.loading") : t("food_sheet.save")}
      </Button>
      <div className="pb-2" />
    </SheetShell>
  );
}

function NumField({ label, value, step, onChange }: {
  label: string; value: number; step: number; onChange: (v: number) => void;
}) {
  return (
    <div className="flex items-center gap-1 flex-1">
      <button
        type="button"
        onClick={() => { hapticTap(); onChange(value - step); }}
        aria-label={`${label} −`}
        className="w-11 h-11 rounded-full bg-surface-strong text-xl leading-none press shrink-0"
      >−</button>
      <input
        type="number"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
        aria-label={label}
        className="w-full min-w-0 bg-transparent text-center tabular-nums"
      />
      <button
        type="button"
        onClick={() => { hapticTap(); onChange(value + step); }}
        aria-label={`${label} +`}
        className="w-11 h-11 rounded-full bg-surface-strong text-xl leading-none press shrink-0"
      >+</button>
    </div>
  );
}
